import { type FC, type ReactNode, lazy, Suspense, type RefObject, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import slugify from 'dibs-slugify/exports/slugify.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpRespStorytellingTab } from '../PdpRespStorytellingModule/PdpRespStorytellingTab.tsx';
import {
    PdpSharedStorytellingTrackingContextProvider,
    PdpSharedStorytellingTrackingWrapper,
} from '../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking.tsx';
import { PdpRespDealerDetails } from '../PdpRespDealerDetails/PdpRespDealerDetails.tsx';
import { PdpRespShippingReturns } from '../PdpRespDetails/PdpRespShippingReturns.tsx';
import { PdpRespCreatorsPanel } from './PdpRespCreatorsPanel.tsx';
import { PdpRespTabsCustomizationPanel } from '../PdpRespDetailTabs/PdpRespTabsCustomizationPanel.tsx';
import { PdpRespAboutItemPanelItemModule } from './PdpRespAboutItemPanelItemModule.tsx';
import {
    getCreatorsAccordionTabs,
    readCreatorsAccordionTabData,
} from '../helpers/getCreatorsAccordionTabs.ts';
import { getCreatorsAccordionTitle } from '../helpers/getCreatorsAccordionTitle.ts';
import { isCreatorsAccordionTab } from '../helpers/isCreatorsAccordionTab.ts';
import { getShowPdpEstimateInTab } from '../helpers/getPdpEstimateSimilarSold.ts';
import { getSelectedSku } from '../helpers/getSelectedSku.ts';
import {
    ITEM_DETAILS,
    ABOUT_SELLER,
    CREATORS,
    CUSTOMIZATION,
    SHIPPING_RETURNS,
} from '../helpers/tabConstants.ts';
import { type RelayVariables, type Store } from '../../global/relayVariableTypes.ts';
import { getAttributeInfo } from '../helpers/getAttributeInfo.ts';
import {
    sellerInformation,
    itemDescriptionAndSpecifications,
    customization,
    shippingAndReturns,
} from '../pdpMessages.tsx';
import { PDP_ITEM_MODULE_ACCORDION_TRIGGER } from '../tracking/pdpItemModuleTracking.ts';
import { PdpRespDetailAccordionItem } from './PdpRespDetailAccordionItem.tsx';

import { type PdpRespDetailAccordions_viewer$key } from './__generated__/PdpRespDetailAccordions_viewer.graphql.ts';
import { type PdpRespDetailAccordions_user$key } from './__generated__/PdpRespDetailAccordions_user.graphql.ts';
import { type PdpRespDetailAccordions_item$data } from './__generated__/PdpRespDetailAccordions_item.graphql.ts';
import { type PdpRespDetailAccordions_item$key } from './__generated__/PdpRespDetailAccordions_item.graphql.ts';
import styles from './PdpRespDetailAccordions.module.css';

type StoryTellingTab = NonNullable<
    NonNullable<NonNullable<PdpRespDetailAccordions_item$data['storyTelling']>['tabs']>[number]
>;

const PdpRespPricingGuidance = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespPricingGuidance" */ '../PdpRespDetails/PdpRespPricingGuidance.tsx'
        )
);

const PdpSharedEstimateSimilarSoldPricing = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedEstimateSimilarSoldPricing" */ '../PdpSharedEstimateSimilarSold/PdpSharedEstimateSimilarSoldPricing.tsx'
        )
);

const viewerFragment = graphql`
    fragment PdpRespDetailAccordions_viewer on Viewer {
        ...PdpRespAboutItemPanelItemModule_viewer
        ...PdpRespDealerDetails_viewer @skip(if: $isPdpSellerModuleTestVariant)
        ...getPdpEstimateSimilarSold_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespDetailAccordions_user on User {
        ...PdpRespAboutItemPanelItemModule_user
        ...PdpRespDealerDetails_user @skip(if: $isPdpSellerModuleTestVariant)
        ...PdpRespShippingReturns_user
        ...PdpRespTabsCustomizationPanel_user
        ...PdpSharedBuyerIncentives_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespDetailAccordions_item on Item {
        # eslint-disable-next-line relay/unused-fields - skus/serviceId is used by getSelectedSku
        skus {
            serviceId
            ... @skip(if: $isPdpSellerModuleTestVariant) {
                ...PdpRespDealerDetails_sku @include(if: $isMultiSku)
            }
            ...PdpRespShippingReturns_sku @include(if: $isMultiSku)
        }
        storyTelling: storyTellingModule(shortTitles: true) {
            tabs {
                tabTitle
                ...getCreatorsAccordionTabs_storyTellingTab
                ...PdpRespStorytellingTab_storyTellingTab
            }
        }
        ...PdpRespCreatorsPanel_item
        seller {
            isAccessible
        }
        isCustomizable
        isDyoCustomizable
        attributes {
            ...getAttributeInfo_itemAttributes
        }
        isListingMigrated
        ...PdpRespPricingGuidance_item @include(if: $afterDisplay)
        ... @include(if: $isEstimateSimilarSoldVariant) {
            ...PdpSharedEstimateSimilarSoldPricing_item @include(if: $afterDisplay)
        }
        ...PdpRespAboutItemPanelItemModule_item
        ...PdpRespDealerDetails_item @skip(if: $isPdpSellerModuleTestVariant)
        ...PdpRespShippingReturns_item
        ...PdpRespTabsCustomizationPanel_item
        ...getPdpEstimateSimilarSold_item
    }
`;

type AccordionSection = {
    key: string;
    summary: ReactNode;
    content: ReactNode;
    defaultOpen?: boolean;
    dataTn?: string;
    contentDataTn?: string;
    trackingTrigger: string;
};

export const PdpRespDetailAccordions: FC<{
    viewer: PdpRespDetailAccordions_viewer$key;
    user: PdpRespDetailAccordions_user$key | null;
    item: PdpRespDetailAccordions_item$key;
    selectedSkuId: string | null;
    itemQuantity: number | null;
    aboutSellerPanelRef: RefObject<HTMLDivElement | null>;
    currency: string;
    requestedOpenSection?: string | null;
    onRequestedOpenSectionHandled?: () => void;
}> = ({
    selectedSkuId,
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    itemQuantity,
    aboutSellerPanelRef,
    currency,
    requestedOpenSection = null,
    onRequestedOpenSectionHandled,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const { isPdpEstimateSimilarSoldTestVariant, isPdpSellerModuleTestVariant } =
        useServerVarsContext();
    const showPdpEstimateInTab = getShowPdpEstimateInTab({
        viewer,
        item,
        currency,
        isPdpEstimateSimilarSoldTestVariant: !!isPdpEstimateSimilarSoldTestVariant,
    });

    const tabs = item?.storyTelling?.tabs ?? [];
    const creatorTabs = getCreatorsAccordionTabs<StoryTellingTab>(tabs);
    const creatorsAccordionTitle = getCreatorsAccordionTitle(
        creatorTabs.map(tab => tab.tabTitle || '').filter(Boolean)
    );
    const otherStoryTabs = tabs.filter((tab): tab is StoryTellingTab => {
        if (!tab) {
            return false;
        }

        return !isCreatorsAccordionTab(readCreatorsAccordionTabData(tab).tabType);
    });
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );
    const { afterDisplay, isMultiSku } = relayVariables;
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const isSellerAccessible = item?.seller?.isAccessible;
    const isListingMigrated = item?.isListingMigrated;

    const { isCustomizable = null, isDyoCustomizable, attributes } = item || {};
    const { customizationOptions = null, isResizable = false } = getAttributeInfo(attributes);

    const showCustomizationOptions =
        (isCustomizable && !!customizationOptions) || isDyoCustomizable || isResizable;

    const sections: AccordionSection[] = [
        {
            key: ITEM_DETAILS,
            summary: itemDescriptionAndSpecifications,
            trackingTrigger: PDP_ITEM_MODULE_ACCORDION_TRIGGER.itemDescriptionAndSpecifications,
            defaultOpen: true,
            dataTn: 'item-details-accordion',
            contentDataTn: 'item-details-accordion-content',
            content: (
                <PdpRespAboutItemPanelItemModule
                    viewer={viewer}
                    user={user}
                    item={item}
                    selectedSkuId={selectedSkuId}
                    afterDisplay={afterDisplay}
                    isAvailableItem
                >
                    {afterDisplay &&
                        isPdpEstimateSimilarSoldTestVariant &&
                        showPdpEstimateInTab && (
                            <Suspense fallback="">
                                <PdpSharedEstimateSimilarSoldPricing
                                    currency={currency}
                                    item={item}
                                    theme="light"
                                    titleClassName={dibsCss.sassyFontHeaderSmall}
                                />
                            </Suspense>
                        )}
                    {afterDisplay && !isPdpEstimateSimilarSoldTestVariant && (
                        <Suspense fallback="">
                            <PdpRespPricingGuidance
                                currency={currency}
                                item={item}
                                skuId={selectedSkuId}
                            />
                        </Suspense>
                    )}
                </PdpRespAboutItemPanelItemModule>
            ),
        },
        ...(creatorTabs.length > 0
            ? [
                  {
                      key: CREATORS,
                      summary: creatorsAccordionTitle,
                      trackingTrigger: creatorsAccordionTitle,
                      dataTn: 'creators-accordion',
                      contentDataTn: 'creators-accordion-content',
                      content: (
                          <PdpSharedStorytellingTrackingWrapper>
                              <PdpRespCreatorsPanel item={item} />
                          </PdpSharedStorytellingTrackingWrapper>
                      ),
                  },
              ]
            : []),
        ...otherStoryTabs.map((tab, index) => {
            const tabTitle = tab.tabTitle;
            const tabKey = `${slugify(tabTitle)}-${index}`;

            return {
                key: tabKey,
                summary: tabTitle,
                trackingTrigger: tabTitle || '',
                dataTn: `${tabKey}-accordion`,
                contentDataTn: `${tabKey}-accordion-content`,
                content: (
                    <PdpSharedStorytellingTrackingWrapper>
                        <PdpRespStorytellingTab
                            showHeader={false}
                            wrapperClass={classnames(dibsCss.m0, dibsCss.py0, dibsCss.pxLarger)}
                            storyTellingTab={tab}
                            clearBackground={false}
                            showExpandingArea
                            dataTn={`${tabKey}-tab-content`}
                        />
                    </PdpSharedStorytellingTrackingWrapper>
                ),
            };
        }),
        ...(showCustomizationOptions
            ? [
                  {
                      key: CUSTOMIZATION,
                      summary: customization,
                      trackingTrigger: PDP_ITEM_MODULE_ACCORDION_TRIGGER.customization,
                      dataTn: 'customization-accordion',
                      contentDataTn: 'customization-accordion-content',
                      content: (
                          <PdpRespTabsCustomizationPanel
                              item={item}
                              user={user || null}
                              isAccordionLayout
                          />
                      ),
                  },
              ]
            : []),
        ...(!isPdpSellerModuleTestVariant && isSellerAccessible
            ? [
                  {
                      key: ABOUT_SELLER,
                      summary: sellerInformation,
                      trackingTrigger: PDP_ITEM_MODULE_ACCORDION_TRIGGER.sellerInformation,
                      dataTn: 'about-the-seller-accordion',
                      contentDataTn: 'about-the-seller-accordion-content',
                      content: (
                          <PdpRespDealerDetails
                              user={user}
                              item={item}
                              sku={isMultiSku && selectedSku ? selectedSku : null}
                              viewer={viewer}
                              currency={currency}
                              afterDisplay={afterDisplay}
                              dataTn="about-the-seller-tab-content"
                              hideBorder
                              panelRef={aboutSellerPanelRef}
                          />
                      ),
                  },
              ]
            : []),
        ...(isSellerAccessible && !isListingMigrated
            ? [
                  {
                      key: SHIPPING_RETURNS,
                      summary: shippingAndReturns,
                      trackingTrigger: PDP_ITEM_MODULE_ACCORDION_TRIGGER.shippingAndReturns,
                      dataTn: 'shipping-and-returns-accordion',
                      contentDataTn: 'shipping-and-returns-accordion-content',
                      content: (
                          <PdpRespShippingReturns
                              currency={currency}
                              user={user}
                              item={item}
                              sku={isMultiSku && selectedSku ? selectedSku : null}
                              itemQuantity={itemQuantity}
                              isAccordionLayout
                          />
                      ),
                  },
              ]
            : []),
    ];

    const handleRequestedOpenSectionHandled = useCallback(() => {
        onRequestedOpenSectionHandled?.();
    }, [onRequestedOpenSectionHandled]);

    return (
        <div className={classnames(dibsCss.mtLarge, dibsCss.wFull)}>
            <div
                className={classnames(
                    'rowFlex',
                    dibsCss.maxW1440px,
                    dibsCss.m0,
                    dibsCss.mxAuto,
                    dibsCss.px0
                )}
            >
                <div className={classnames('colXs12', 'colLg7', 'colXl8', dibsCss.px0)}>
                    <PdpSharedStorytellingTrackingContextProvider>
                        <ul className={classnames(styles.listNone, dibsCss.m0, dibsCss.p0)}>
                            {sections.map((section, index) => (
                                <PdpRespDetailAccordionItem
                                    key={section.key}
                                    summary={section.summary}
                                    defaultOpen={section.defaultOpen}
                                    dataTn={section.dataTn}
                                    contentDataTn={section.contentDataTn}
                                    forceOpen={requestedOpenSection === section.key}
                                    onForceOpenHandled={handleRequestedOpenSectionHandled}
                                    isLast={index === sections.length - 1}
                                    trackingTrigger={section.trackingTrigger}
                                >
                                    {section.content}
                                </PdpRespDetailAccordionItem>
                            ))}
                        </ul>
                    </PdpSharedStorytellingTrackingContextProvider>
                </div>
            </div>
        </div>
    );
};
